import { BrebKeyType } from "../services/paymentsPayout.service";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALIAS = /^@[a-zA-Z0-9._-]{3,63}$/;
const DIGITS = /^\d{6,20}$/;
const PLAIN = /^(?=.*[a-zA-Z])[a-zA-Z0-9]{5,64}$/;

/**
 * A key typed without saying which kind it is, reduced to the bare value the directory resolves.
 *
 * Only an all-digit value loses its separators and a `+57`/`57` prefix in front of a mobile, because only
 * there they are formatting; an alias or a merchant code keeps every character it was typed with.
 */
export function normalizeBrebLookupValue(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("@")) return trimmed.replace(/\s+/g, "");
  if (trimmed.includes("@")) return trimmed.replace(/\s+/g, "").toLowerCase();
  const compact = trimmed.replace(/[\s.()-]/g, "");
  if (/^\+?\d+$/.test(compact)) {
    const digits = compact.replace("+", "");
    return /^57(3\d{9})$/.exec(digits)?.[1] ?? digits;
  }
  return trimmed.replace(/\s+/g, "");
}

/** Whether a normalized value is worth asking the directory about. */
export function looksLikeBrebKey(normalized: string): boolean {
  return (
    EMAIL.test(normalized) || ALIAS.test(normalized) || DIGITS.test(normalized) || PLAIN.test(normalized)
  );
}

/** A guess from the value's shape, for when the directory answers without a key type. Never MERCHANT. */
export function inferBrebKeyType(value: string): BrebKeyType {
  const v = value.trim();
  if (EMAIL.test(v)) return BrebKeyType.EMAIL;
  if (v.startsWith("@")) return BrebKeyType.ALPHANUM;
  const digits = v.replace(/\D/g, "");
  if (digits.length === v.length && /^3\d{9}$/.test(digits)) return BrebKeyType.PHONE;
  if (/^\d{5,20}$/.test(v)) return BrebKeyType.NRIC;
  return BrebKeyType.ALPHANUM;
}

/** The directory's own key type when it is one the payout accepts, the shape's guess otherwise. */
export function resolvedBrebKeyType(directoryKeyType: string | null | undefined, value: string): BrebKeyType {
  const reported = String(directoryKeyType ?? "")
    .trim()
    .toUpperCase();
  return (Object.values(BrebKeyType) as string[]).includes(reported)
    ? (reported as BrebKeyType)
    : inferBrebKeyType(value);
}

/** The key as the sender typed it, readable: a mobile grouped 3-3-4, anything else untouched. */
export function displayBrebKey(type: BrebKeyType, value: string): string {
  const digits = value.replace(/\D/g, "");
  if (type === BrebKeyType.PHONE && /^3\d{9}$/.test(digits)) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return value.trim();
}
